import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Target, Crown, Building, Bot, Zap, Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import type { ResellerConfig, AiPricingTier } from "@shared/schema";

interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  price: number;
  interval: string;
  maxUsers: number;
  maxGoals: number;
  features: string[];
  stripePriceId: string;
  isActive: boolean;
}

export default function Pricing() {
  const { isAuthenticated } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const { data: plans = [], isLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/subscription-plans"],
  });

  const { data: config } = useQuery<ResellerConfig>({
    queryKey: ["/api/reseller/config"],
    retry: false,
  });

  const { data: aiPricingTiers = [], isLoading: loadingAiTiers } = useQuery<AiPricingTier[]>({
    queryKey: ["/api/reseller/ai-pricing"],
    retry: false,
  });

  // Use configuration values or fallbacks
  const companyName = config?.companyName || "GoalVia";
  const primaryColor = config?.primaryColor || "#3b82f6";
  const showPricing = config?.showPricing ?? true;
  const customPricingMessage = config?.customPricingMessage;

  const handleSubscribe = async (planId: string) => {
    if (!isAuthenticated) {
      window.location.href = "/api/login";
      return;
    }

    try {
      setSelectedPlan(planId);
      const response = await fetch("/api/create-subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ planId }),
      });

      if (!response.ok) {
        throw new Error("Failed to create subscription");
      }

      const { clientSecret } = await response.json();
      
      // Redirect to Stripe checkout or handle payment
      window.location.href = `/subscribe?client_secret=${clientSecret}`;
    } catch (error) {
      console.error("Subscription error:", error);
      setSelectedPlan(null);
    }
  };

  const getPlanIcon = (planName: string) => {
    switch (planName.toLowerCase()) {
      case "free": return <Target className="h-6 w-6" />;
      case "pro": return <Crown className="h-6 w-6" />;
      case "enterprise": return <Building className="h-6 w-6" />;
      default: return <Target className="h-6 w-6" />;
    }
  };

  const getPlanColor = (planName: string) => {
    switch (planName.toLowerCase()) {
      case "free": return "text-gray-600";
      case "pro": return "text-primary";
      case "enterprise": return "text-purple-600";
      default: return "text-gray-600";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="border-b bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              {config?.companyLogo ? (
                <img 
                  src={config.companyLogo} 
                  alt={companyName} 
                  className="h-8 w-auto"
                  data-testid="img-company-logo"
                />
              ) : (
                <Target className="h-8 w-8" style={{ color: primaryColor }} />
              )}
              <span className="text-2xl font-bold" data-testid="text-company-name">{companyName}</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => window.location.href = "/"} data-testid="button-back-home">
                Home
              </Button>
              {!isAuthenticated ? (
                <Button onClick={() => window.location.href = "/api/login"} data-testid="button-login">
                  Sign In
                </Button>
              ) : (
                <Button onClick={() => window.location.href = "/dashboard"} data-testid="button-dashboard">
                  Dashboard
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Start free, upgrade when you're ready to unlock more potential
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
              <Card key={plan.id} className={`relative ${plan.name === "pro" ? "border-primary shadow-lg scale-105" : ""}`}>
                {plan.name === "pro" && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
                    Most Popular
                  </Badge>
                )}
                
                <CardHeader className="text-center">
                  <div className={`mx-auto mb-4 ${getPlanColor(plan.name)}`}>
                    {getPlanIcon(plan.name)}
                  </div>
                  <CardTitle className="text-2xl">{plan.displayName}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">
                      {plan.price === 0 ? "Free" : `$${plan.price / 100}`}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-gray-500">/{plan.interval}</span>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {(plan.features || []).map((feature, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4">
                    <Button 
                      className="w-full" 
                      variant={plan.name === "pro" ? "default" : "outline"}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={selectedPlan === plan.id}
                      data-testid={`button-select-${plan.name}`}
                    >
                      {selectedPlan === plan.id ? "Processing..." : plan.price === 0 ? "Get Started" : "Subscribe"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* AI Pricing Section */}
          {aiPricingTiers.length > 0 && (
            <div className="mt-20">
              <div className="text-center mb-16">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Bot className="h-8 w-8" style={{ color: primaryColor }} />
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                    AI Features & Usage
                  </h2>
                </div>
                <p className="text-xl text-gray-600 dark:text-gray-300">
                  Powerful AI capabilities with transparent usage-based pricing
                </p>
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg max-w-3xl mx-auto">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Zap className="h-5 w-5" style={{ color: primaryColor }} />
                    <span className="font-semibold" style={{ color: primaryColor }}>Replit-Style Billing Model</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Monthly minimum charge + variable token usage. You only pay for what you use beyond the included tokens.
                  </p>
                </div>
                {customPricingMessage && (
                  <p className="text-lg font-medium mt-4" style={{ color: primaryColor }} data-testid="text-ai-pricing-message">
                    {customPricingMessage}
                  </p>
                )}
              </div>

              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {aiPricingTiers
                  .filter(tier => tier.isActive)
                  .sort((a, b) => a.priority - b.priority)
                  .map((tier) => (
                  <Card key={tier.id} className={`relative ${tier.isRecommended ? "border-primary shadow-lg scale-105" : ""}`}>
                    {tier.isRecommended && (
                      <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2" style={{ backgroundColor: primaryColor }}>
                        <Star className="w-3 h-3 mr-1" />
                        Recommended
                      </Badge>
                    )}
                    
                    <CardHeader className="text-center">
                      <div className="mx-auto mb-4">
                        <Bot className="h-8 w-8" style={{ color: primaryColor }} />
                      </div>
                      <CardTitle className="text-2xl">{tier.displayName}</CardTitle>
                      <CardDescription>{tier.description}</CardDescription>
                      <div className="mt-4 space-y-2">
                        <div>
                          <span className="text-3xl font-bold">
                            ${tier.monthlyMinimum}
                          </span>
                          <span className="text-gray-500">/month minimum</span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Includes {tier.tokensIncluded.toLocaleString()} tokens
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">${tier.pricePerThousandTokens}</span> per 1K additional tokens
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <div className="space-y-3">
                        {tier.enableJournalAI && (
                          <div className="flex items-start space-x-3">
                            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">AI Journal Insights</span>
                          </div>
                        )}
                        {tier.enableGoalSuggestions && (
                          <div className="flex items-start space-x-3">
                            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Smart Goal Suggestions</span>
                          </div>
                        )}
                        {tier.enableInsights && (
                          <div className="flex items-start space-x-3">
                            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Productivity Insights</span>
                          </div>
                        )}
                        {tier.enableAdvancedAnalytics && (
                          <div className="flex items-start space-x-3">
                            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Advanced Analytics</span>
                          </div>
                        )}
                        
                        <div className="pt-2 border-t">
                          <div className="text-xs text-gray-500 space-y-1">
                            <div>Rate Limits:</div>
                            <div>• {tier.maxRequestsPerMinute}/min</div>
                            <div>• {tier.maxRequestsPerDay}/day</div>
                            {tier.maxTokensPerMonth && (
                              <div>• {tier.maxTokensPerMonth.toLocaleString()} tokens/month max</div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="pt-4">
                        <Button 
                          className="w-full" 
                          variant={tier.isRecommended ? "default" : "outline"}
                          style={tier.isRecommended ? { backgroundColor: primaryColor } : { borderColor: primaryColor, color: primaryColor }}
                          onClick={() => handleSubscribe(tier.id)}
                          disabled={selectedPlan === tier.id}
                          data-testid={`button-select-ai-${tier.name}`}
                        >
                          {selectedPlan === tier.id ? "Processing..." : "Choose Plan"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="mt-12 text-center">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 max-w-2xl mx-auto">
                  <h3 className="text-lg font-semibold mb-3">How AI Billing Works</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                    <div>
                      <strong>Monthly Minimum:</strong> Guaranteed base charge each month
                    </div>
                    <div>
                      <strong>Variable Usage:</strong> Pay only for tokens beyond included amount
                    </div>
                    <div>
                      <strong>Transparent Pricing:</strong> Real-time usage tracking in dashboard
                    </div>
                    <div>
                      <strong>No Surprises:</strong> Set spending limits and usage alerts
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
              Frequently Asked Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Can I change plans anytime?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300">
                    Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, 
                    and we'll prorate any billing adjustments.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">What happens to my data if I cancel?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300">
                    Your data is safely stored for 30 days after cancellation. You can reactivate 
                    your account and restore all your goals, habits, and journal entries.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Do you offer team discounts?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300">
                    Yes! Enterprise plans include volume discounts for teams of 10+ users. 
                    Contact our sales team for custom pricing.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Is my data secure?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300">
                    Absolutely. We use bank-level encryption, regular security audits, 
                    and comply with GDPR and SOC 2 standards.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}